import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useIntake } from '../../context/IntakeContext';
import { RadioGroup } from '../ui/RadioGroup';
import { FileUpload } from '../ui/FileUpload';
import { ColorPicker } from '../ui/ColorPicker';
import { Textarea } from '../ui/Textarea';
import { MascotPanel } from '../ui/MascotPanel';
import { CheckIcon } from 'lucide-react';
const fontCategories = [{
  category: 'Modern & Strak',
  fonts: [{
    value: 'inter',
    label: 'Inter',
    family: 'Inter, system-ui, sans-serif',
    weight: 400
  }, {
    value: 'roboto',
    label: 'Roboto',
    family: 'Roboto, sans-serif',
    weight: 400
  }, {
    value: 'montserrat',
    label: 'Montserrat',
    family: 'Montserrat, sans-serif',
    weight: 400
  }, {
    value: 'dm_sans',
    label: 'DM Sans',
    family: '"DM Sans", sans-serif',
    weight: 400
  }, {
    value: 'albert_sans',
    label: 'Albert Sans',
    family: '"Albert Sans", sans-serif',
    weight: 400
  }, {
    value: 'nunito_sans',
    label: 'Nunito Sans',
    family: '"Nunito Sans", sans-serif',
    weight: 400
  }]
}, {
  category: 'Klassiek & Elegant',
  fonts: [{
    value: 'playfair',
    label: 'Playfair Display',
    family: '"Playfair Display", serif',
    weight: 400
  }, {
    value: 'merriweather',
    label: 'Merriweather',
    family: 'Merriweather, serif',
    weight: 400
  }, {
    value: 'lora',
    label: 'Lora',
    family: 'Lora, serif',
    weight: 400
  }, {
    value: 'cormorant',
    label: 'Cormorant Garamond',
    family: '"Cormorant Garamond", serif',
    weight: 400
  }, {
    value: 'libre_baskerville',
    label: 'Libre Baskerville',
    family: '"Libre Baskerville", serif',
    weight: 400
  }]
}, {
  category: 'Speels & Creatief',
  fonts: [{
    value: 'poppins',
    label: 'Poppins',
    family: 'Poppins, sans-serif',
    weight: 400
  }, {
    value: 'quicksand',
    label: 'Quicksand',
    family: 'Quicksand, sans-serif',
    weight: 400
  }, {
    value: 'comfortaa',
    label: 'Comfortaa',
    family: 'Comfortaa, sans-serif',
    weight: 400
  }, {
    value: 'fredoka',
    label: 'Fredoka',
    family: 'Fredoka, sans-serif',
    weight: 400
  }, {
    value: 'baloo2',
    label: 'Baloo 2',
    family: '"Baloo 2", sans-serif',
    weight: 400
  }]
}, {
  category: 'Zakelijk & Professioneel',
  fonts: [{
    value: 'source_sans',
    label: 'Source Sans 3',
    family: '"Source Sans 3", sans-serif',
    weight: 400
  }, {
    value: 'open_sans',
    label: 'Open Sans',
    family: '"Open Sans", sans-serif',
    weight: 400
  }, {
    value: 'work_sans',
    label: 'Work Sans',
    family: '"Work Sans", sans-serif',
    weight: 400
  }, {
    value: 'ibm_plex',
    label: 'IBM Plex Sans',
    family: '"IBM Plex Sans", sans-serif',
    weight: 400
  }, {
    value: 'barlow',
    label: 'Barlow',
    family: 'Barlow, sans-serif',
    weight: 400
  }]
}, {
  category: 'Minimalistisch',
  fonts: [{
    value: 'raleway',
    label: 'Raleway',
    family: 'Raleway, sans-serif',
    weight: 300
  }, {
    value: 'josefin_sans',
    label: 'Josefin Sans',
    family: '"Josefin Sans", sans-serif',
    weight: 300
  }, {
    value: 'karla',
    label: 'Karla',
    family: 'Karla, sans-serif',
    weight: 300
  }, {
    value: 'manrope',
    label: 'Manrope',
    family: 'Manrope, sans-serif',
    weight: 300
  }]
}, {
  category: 'Stoer & Bold',
  fonts: [{
    value: 'oswald',
    label: 'Oswald',
    family: 'Oswald, sans-serif',
    weight: 700
  }, {
    value: 'bebas_neue',
    label: 'Bebas Neue',
    family: '"Bebas Neue", sans-serif',
    weight: 400
  }, {
    value: 'anton',
    label: 'Anton',
    family: 'Anton, sans-serif',
    weight: 400
  }, {
    value: 'russo_one',
    label: 'Russo One',
    family: '"Russo One", sans-serif',
    weight: 400
  }]
}];
function FontSelector({
  value,
  onChange
}: {
  value: string;
  onChange: (val: string) => void;
}) {
  return <div className="space-y-6" data-id="element-569" data-mp="2k7idk21uiy7t">
      {fontCategories.map(cat => <div key={cat.category} data-id="element-570" data-mp="2f974vunaetr6">
          <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-571" data-mp="2fccst6k6jplb">
            {cat.category}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-572" data-mp="2rskvxh8ftt81">
            {cat.fonts.map(font => {
          const isSelected = value === font.value;
          return <motion.button key={font.value} type="button" whileHover={{
            scale: 1.01
          }} whileTap={{
            scale: 0.99
          }} onClick={() => onChange(font.value)} className={`relative flex items-center justify-between p-4 text-left transition-all duration-200 border-2 rounded-xl w-full ${isSelected ? 'border-[#007AFF] bg-[#F0F7FF] shadow-md' : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-573" data-mp="2huol6qjex0ag">
                  <div className="flex-1" data-id="element-574" data-mp="3t9ldqaet27mk">
                    <span className={`text-lg block mb-0.5 ${isSelected ? 'text-[#007AFF]' : 'text-gray-900'}`} style={{
                fontFamily: font.family,
                fontWeight: font.weight
              }} data-id="element-575" data-mp="3tamxr4dsu4gf">
                      Aa Bb Cc 123
                    </span>
                    <span className={`text-xs ${isSelected ? 'text-[#005FCC]' : 'text-gray-500'}`} data-id="element-576" data-mp="3p6u8tdu94879">
                      {font.label}
                    </span>
                  </div>
                  <div className={`flex items-center justify-center w-5 h-5 rounded-full border-2 flex-shrink-0 ml-3 ${isSelected ? 'border-[#007AFF] bg-[#007AFF]' : 'border-gray-300'}`} data-id="element-577" data-mp="3feoi07fqjwtn">
                    {isSelected && <CheckIcon className="w-3 h-3 text-white" data-id="element-578" data-mp="31ggns7a339m6" />}
                  </div>
                </motion.button>;
        })}
          </div>
        </div>)}

      <motion.button type="button" whileHover={{
      scale: 1.01
    }} whileTap={{
      scale: 0.99
    }} onClick={() => onChange('advise')} className={`relative flex items-center justify-between p-4 text-left transition-all duration-200 border-2 rounded-xl w-full ${value === 'advise' ? 'border-[#007AFF] bg-[#F0F7FF] shadow-md' : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-579" data-mp="2wjpyctce31o3">
        <div data-id="element-580" data-mp="23oig5idcaufe">
          <span className={`font-semibold ${value === 'advise' ? 'text-[#007AFF]' : 'text-gray-900'}`} data-id="element-581" data-mp="23kujiacllrwg">
            Weet ik niet / Adviseer mij
          </span>
          <span className={`text-sm block mt-0.5 ${value === 'advise' ? 'text-[#005FCC]' : 'text-gray-500'}`} data-id="element-582" data-mp="2djk5ae7m7whz">
            Wij kiezen een passend lettertype voor u
          </span>
        </div>
        <div className={`flex items-center justify-center w-5 h-5 rounded-full border-2 flex-shrink-0 ml-3 ${value === 'advise' ? 'border-[#007AFF] bg-[#007AFF]' : 'border-gray-300'}`} data-id="element-583" data-mp="1kud75gc2p8x9">
          {value === 'advise' && <CheckIcon className="w-3 h-3 text-white" data-id="element-584" data-mp="300oyjg96sgsw" />}
        </div>
      </motion.button>
    </div>;
}
export function Step7Branding() {
  const {
    data,
    updateData
  } = useIntake();
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -20
  }} className="max-w-3xl mx-auto space-y-8" data-id="element-585" data-mp="3913nqqp9ug1o">
      <MascotPanel message="De visuele identiteit bepaalt de eerste indruk. Heeft u al een logo en huisstijl, of mogen wij iets moois voor u ontwerpen?" data-id="element-586" data-mp="3iyi46qb734tz" />

      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 space-y-8" data-id="element-587" data-mp="3rrv4wefi3aqm">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-588" data-mp="ajhmmq9kzeki">
          Branding & Huisstijl
        </h2>

        <div data-id="element-589" data-mp="5kn3tobm5q0e">
          <label className="block text-sm font-semibold text-gray-900 mb-3" data-id="element-590" data-mp="5kwttbwnrx63">
            Heeft u al een visuele identiteit?
          </label>
          <RadioGroup columns={1} value={data.hasIdentity} onChange={val => updateData({
          hasIdentity: val
        })} options={[{
          value: 'yes_complete',
          label: 'Ja, ik heb een compleet logo en huisstijlhandboek'
        }, {
          value: 'yes_partly',
          label: 'Ja, ik heb een logo maar verder geen vaste stijl'
        }, {
          value: 'no',
          label: 'Nee, Noord Digital mag een logo/huisstijl ontwerpen'
        }]} data-id="element-591" data-mp="p6yezv61pbdh" />
        </div>

        <AnimatePresence data-id="element-592" data-mp="1gua7kh4f5kw">
          {(data.hasIdentity === 'yes_complete' || data.hasIdentity === 'yes_partly') && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} className="space-y-6 pt-4 border-t border-gray-100 overflow-hidden" data-id="element-593" data-mp="3nnsu9chxxthk">
              <h3 className="font-semibold text-gray-900" data-id="element-594" data-mp="30eguq5hm9h2b">
                Upload uw bestanden
              </h3>
              <p className="text-sm text-gray-500 mb-4" data-id="element-595" data-mp="3o4b7kn1xr5mk">
                U kunt deze ook later nasturen als u ze nu niet bij de hand
                heeft.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-596" data-mp="3ef1dx2rg1ewc">
                <FileUpload label="Logo (bij voorkeur SVG of PNG)" uploadKey="logo" value={data.brandingAssets['logo']?.file} onChange={url => updateData({
              brandingAssets: {
                ...data.brandingAssets,
                logo: {
                  file: url,
                  status: 'uploaded'
                }
              }
            })} data-id="element-597" data-mp="3ecoyoxd2limz" />
                {data.hasIdentity === 'yes_complete' && <FileUpload label="Huisstijlhandboek (PDF)" accept=".pdf" uploadKey="guidelines" value={data.brandingAssets['guidelines']?.file} onChange={url => updateData({
              brandingAssets: {
                ...data.brandingAssets,
                guidelines: {
                  file: url,
                  status: 'uploaded'
                }
              }
            })} data-id="element-598" data-mp="25657lfhlk9o1" />}
              </div>

              <div className="pt-4" data-id="element-599" data-mp="2xwwxxwbze43g">
                <ColorPicker colors={data.preferredColors} onChange={colors => updateData({
              preferredColors: colors
            })} label="Wat zijn uw huidige merkkleuren?" data-id="element-600" data-mp="1ukg6e6kihr5x" />
              </div>

              <div className="pt-4" data-id="element-601" data-mp="28imw2zfb4kui">
                <label className="block text-sm font-semibold text-gray-900 mb-3" data-id="element-602" data-mp="1bt5epn6ypacz">
                  Heeft u specifieke wensen qua typografie (lettertypes)?
                </label>
                <FontSelector value={data.typographyChoice} onChange={val => updateData({
              typographyChoice: val
            })} data-id="element-603" data-mp="1gpvwqg2bug11" />
              </div>
            </motion.div>}

          {data.hasIdentity === 'no' && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} className="space-y-6 pt-4 border-t border-gray-100 overflow-hidden" data-id="element-604" data-mp="x37oz2tdwclo">
              <h3 className="font-semibold text-gray-900" data-id="element-605" data-mp="bkgwnau9juv6">Ontwerpvoorkeuren</h3>
              <p className="text-sm text-gray-500 mb-4" data-id="element-606" data-mp="6mxj6d5e3m4a">
                Geef ons wat richting voor het nieuwe ontwerp.
              </p>

              <Textarea label="Heeft u al ideeën voor een logo?" placeholder="Bijv. een strak woordmerk, of een symbool van een boom..." value={data.logoIdeas} onChange={e => updateData({
            logoIdeas: e.target.value
          })} data-id="element-607" data-mp="1t2qwlbpxytl" />

              <FileUpload label="Upload een schets, voorbeeld of inspiratie voor uw logo" uploadKey="logo_sketch" value={data.logoSketchUpload} onChange={url => updateData({
            logoSketchUpload: url
          })} data-id="element-608" data-mp="3f30edjfbfibq" />

              <ColorPicker colors={data.preferredColors} onChange={colors => updateData({
            preferredColors: colors
          })} label="Heeft u voorkeur voor bepaalde kleuren?" data-id="element-609" data-mp="1e7krpe8eu4fh" />

              <div className="pt-4" data-id="element-610" data-mp="qaezzlncudcv">
                <label className="block text-sm font-semibold text-gray-900 mb-3" data-id="element-611" data-mp="qbgbfr8a91v4">
                  Heeft u specifieke wensen qua typografie (lettertypes)?
                </label>
                <FontSelector value={data.typographyChoice} onChange={val => updateData({
              typographyChoice: val
            })} data-id="element-612" data-mp="2koy4tuvdcsy2" />
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.div>;
}